import type { QueryClient } from "@tanstack/react-query";
import { beginPrivateSessionChange, getPrivateSessionState } from "@/lib/api/private-session";
import { signOutSellerFirebaseAuth } from "@/lib/firebase/auth-session";
import { inspectPrivateBinding, refreshAccessScopedQueries, resetPrivateState, recoverPrivateBinding, sessionChannel } from "./sessionChannel";
import { classifyAccessChange } from "@/shared/access/store-access";
import { queryKeys } from "@/lib/query/core/query-keys";
import type { SellerAuthSessionResponse } from "@/lib/api/types";

export async function changePrivateSession<T>(client: QueryClient, operation: (generation: number) => Promise<T>, _storeSlug?: string): Promise<T> {
  beginPrivateSessionChange();
  const generation = getPrivateSessionState().generation;
  const assertOwnedChange = () => {
    const current = getPrivateSessionState();
    if (current.generation !== generation || !current.changing) throw new DOMException("Session change superseded", "AbortError");
  };
  const end = sessionChannel.begin();
  try {
    await resetPrivateState(client);
    assertOwnedChange();
    await signOutSellerFirebaseAuth();
    assertOwnedChange();
    const result = await operation(generation);
    assertOwnedChange();
    return result;
  } finally {
    try {
      if (generation === getPrivateSessionState().generation) await recoverPrivateBinding(client, generation);
    } catch { /* Unknown binding stays paused; recovery retries only the session read. */ }
    end();
    if (getPrivateSessionState().changing) sessionChannel.recover();
  }
}

const accessInspections = new WeakMap<QueryClient, Promise<void>>();

/**
 * Why the backend refused: `membership` (STORE_MEMBERSHIP_INACTIVE — this
 * actor lost the store) or `capability` (STORE_CAPABILITY_REQUIRED — the store
 * lost a feature).
 */
export type AccessRefusal = "membership" | "capability";

/**
 * Rebind the existing cookie after store access changes, without signing out the identity.
 *
 * A membership refusal clears private data before it inspects anything: the
 * actor may no longer read this store at all. A capability refusal first asks
 * `/me` whether the actor is still admitted to the same store; if so the access
 * context updates in place (no remount, unsaved forms survive) and only stale
 * data is refreshed. Anything else — another actor or store, a pause, or an
 * inspection that fails — falls through to the full rebind.
 */
export function reinspectPrivateAccess(client: QueryClient, reason: AccessRefusal = "membership"): Promise<void> {
  const pending = accessInspections.get(client);
  if (pending) return pending;
  // A store switch or another owned transition already performs the same inspection.
  if (getPrivateSessionState().changing) return Promise.resolve();
  const rebind = () => changePrivateSession(client, async () => undefined);
  const inspection = (reason === "capability"
    ? updateAccessInPlace(client).then((done) => (done ? undefined : rebind()))
    : rebind()
  ).finally(() => {
    if (accessInspections.get(client) === inspection) accessInspections.delete(client);
  });
  accessInspections.set(client, inspection);
  return inspection;
}

/** True when the change was absorbed in place (or superseded by another transition). */
async function updateAccessInPlace(client: QueryClient): Promise<boolean> {
  const before = getPrivateSessionState();
  if (!before.storeSlug) return false;
  const sessionKey = queryKeys.seller.session(before.storeSlug);
  const cached = client.getQueryData<SellerAuthSessionResponse>(sessionKey);
  if (!cached) return false;
  let payload: SellerAuthSessionResponse | undefined;
  try {
    payload = await inspectPrivateBinding();
  } catch {
    return false;
  }
  if (!payload) return false;
  const now = getPrivateSessionState();
  // Another transition owns the session now; it performs its own inspection.
  if (now.changing || now.generation !== before.generation) return true;
  const change = classifyAccessChange(cached.seller, payload.seller);
  if (change === "rebind") return false;
  client.setQueryData<SellerAuthSessionResponse>(sessionKey, {
    ...payload,
    // The binding inspection does not mint a Firebase token; keep the one the
    // portal already holds rather than tearing FCM down for an access change.
    firebaseToken: payload.firebaseToken ?? cached.firebaseToken,
  });
  if (change === "in-place") refreshAccessScopedQueries(client);
  return true;
}
